import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '../../lib/cn';

/**
 * A panel. Flat by default, bounded by a border, never by a shadow.
 *
 * Two rules are enforced here rather than remembered:
 *
 * - **No card nests inside a card.** Depth inside a panel is made with
 *   `CardSection` rules and with space. The dev build warns if you try.
 * - **No coloured left-edge stripe, ever.** Status lives in a chip, not in the
 *   furniture. There is no `tone` prop, deliberately.
 */
const card = cva(['rounded-panel border shadow-flush'], {
  variants: {
    surface: {
      default: 'border-border bg-surface text-ink',
      /** Sits on the slate chassis: the sticky consequence bar, the rail. */
      chassis: 'border-transparent bg-chassis text-ink-inverse',
      /** A quiet inset area — a definition strip, a footnote block. */
      sunk: 'border-hairline bg-surface-sunk text-ink',
    },
    padding: {
      /** 16 px on the desktop, 14 px on the phone. */
      default: 'p-4 md:p-4',
      phone: 'p-[14px]',
      /** For a panel whose sections carry their own padding. */
      none: 'p-0',
    },
  },
  defaultVariants: { surface: 'default', padding: 'default' },
});

export type CardVariants = VariantProps<typeof card>;

export interface CardProps extends HTMLAttributes<HTMLDivElement>, CardVariants {
  /**
   * Renders a `<section>` with this accessible name instead of a plain `<div>`.
   * A panel a supervisor navigates to by landmark needs one; a decorative
   * container does not.
   */
  readonly label?: string;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { surface, padding, label, className, children, ...rest },
  ref,
) {
  const classes = cn(card({ surface, padding }), className);

  if (label !== undefined) {
    return (
      <section {...rest} ref={ref} aria-label={label} className={classes}>
        {children}
      </section>
    );
  }

  return (
    <div {...rest} ref={ref} className={classes}>
      {children}
    </div>
  );
});

export interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Rendered at `title-2` — panel and dialog scale. */
  readonly title: ReactNode;
  readonly description?: ReactNode;
  /** Right-aligned controls. */
  readonly actions?: ReactNode;
  /** Heading level. A panel inside a page is usually an `h2` or `h3`. */
  readonly headingLevel?: 2 | 3 | 4;
}

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(function CardHeader(
  { title, description, actions, headingLevel = 3, className, ...rest },
  ref,
) {
  const Heading = `h${headingLevel}` as const;

  return (
    <div
      {...rest}
      ref={ref}
      className={cn('flex flex-wrap items-start justify-between gap-3', className)}
    >
      <div className="min-w-0">
        <Heading className="text-title-2 text-ink">{title}</Heading>
        {description !== undefined ? (
          <p className="mt-1 text-support text-ink-muted">{description}</p>
        ) : null}
      </div>
      {actions !== undefined ? (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      ) : null}
    </div>
  );
});

export interface CardSectionProps extends HTMLAttributes<HTMLDivElement> {
  /** Draws the 1 px hairline above this section. */
  readonly divided?: boolean;
}

/**
 * Internal structure inside a panel: a 1 px hairline with 9–11 px of padding
 * either side. This is what you reach for instead of a nested card.
 */
export const CardSection = forwardRef<HTMLDivElement, CardSectionProps>(function CardSection(
  { divided = true, className, ...rest },
  ref,
) {
  return (
    <div
      {...rest}
      ref={ref}
      className={cn('py-[10px]', divided && 'border-t border-hairline', className)}
    />
  );
});
